import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Award, HelpCircle, ExternalLink, Mail, Eye, EyeOff } from "lucide-react";

const NSE_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8];
const NSE_REQUIRED = [1, 2, 3];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    email: "",
    password: "",
    partner_portal_id: "",
  });
  const [nse, setNse] = useState(new Set());
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleNse = (n) => {
    setNse((s) => {
      const next = new Set(s);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  const missingRequired = NSE_REQUIRED.filter((n) => !nse.has(n));
  const formValid = !missingRequired.length && form.partner_portal_id.trim().length >= 3;

  const submit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      toast.error("Vérifiez les NSE 1, 2, 3 et l'identifiant PartnerPortal.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        nse_certifications: Array.from(nse).sort((a, b) => a - b),
      };
      const r = await register(payload);
      if (r.token) {
        toast.success("Compte créé et activé.");
        nav("/configurator");
      } else {
        setSubmitted(r.user);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center px-4 bg-[#F8F9FA]">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-10 text-center" data-testid="register-pending-card">
          <CheckCircle2 className="h-12 w-12 text-amber-500 mx-auto" />
          <h1 className="mt-4 text-xl font-semibold text-zinc-900">Demande envoyée</h1>
          <p className="mt-2 text-sm text-zinc-600">
            Votre compte <span className="font-mono">{submitted.email}</span> est en attente de validation
            par un administrateur Fortinet.
          </p>
          <div className="mt-6 text-left text-xs space-y-2 bg-zinc-50 border border-zinc-200 rounded-sm p-4">
            <p><span className="font-semibold text-zinc-700">PartnerPortal ID :</span> <span className="font-mono">{submitted.partner_portal_id || "—"}</span></p>
            <p><span className="font-semibold text-zinc-700">Certifications NSE :</span> {(submitted.nse_certifications || []).map((n) => `NSE ${n}`).join(", ") || "—"}</p>
            <p><span className="font-semibold text-zinc-700">Statut :</span> <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 rounded-sm font-medium">En attente</span></p>
          </div>
          <p className="mt-6 text-xs text-zinc-500">
            Vous serez notifié une fois la vérification PartnerPortal + certifications NSE effectuée.
          </p>
          <Link to="/" className="block mt-6 text-sm text-[#D42B1E] font-medium" data-testid="register-pending-back-link">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-56px)] flex items-center justify-center px-4 py-10 bg-[#F8F9FA]">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 justify-center mb-8">
          <img src="/pythagore-logo.svg" alt="Pythagore" className="h-9" />
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-8">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Créer votre compte Pythagore</h1>
          <p className="mt-1 text-sm text-zinc-600">
            Réservé aux commerciaux et avant-ventes partenaires titulaires d'un compte PartnerPortal.
          </p>

          {/* Eligibility banner */}
          <div className="mt-5 border border-amber-200 bg-amber-50 rounded-sm p-4 flex gap-3" data-testid="register-eligibility-banner">
            <ShieldCheck className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900 space-y-1">
              <p className="font-semibold uppercase tracking-wide">Conditions d'accès au configurateur</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Disposer d'un compte actif sur <span className="font-mono">partnerportal.fortinet.com</span> (PartnerPortal Fortinet)</li>
                <li>Avoir validé au minimum les certifications <strong>NSE 1, NSE 2 et NSE 3</strong></li>
                <li>Compte vérifié manuellement par un administrateur avant activation</li>
              </ul>
            </div>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Nom complet</Label>
                <Input required value={form.full_name} onChange={onChange("full_name")} className="mt-1 rounded-sm" data-testid="register-name-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Société</Label>
                <Input value={form.company} onChange={onChange("company")} className="mt-1 rounded-sm" data-testid="register-company-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Email professionnel</Label>
                <Input type="email" required value={form.email} onChange={onChange("email")} className="mt-1 rounded-sm" data-testid="register-email-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">Mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPw ? "text" : "password"}
                    required
                    minLength={6}
                    value={form.password}
                    onChange={onChange("password")}
                    className="rounded-sm pr-10"
                    data-testid="register-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute inset-y-0 right-0 h-full w-10 inline-flex items-center justify-center text-zinc-400 hover:text-zinc-700 focus:outline-none"
                    aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    aria-pressed={showPw}
                    title={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    data-testid="register-password-toggle"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-zinc-500 leading-snug" data-testid="register-password-hint">
                  Choisissez vous-même un mot de passe (6 caractères minimum). Mélangez majuscules, chiffres et symboles pour plus de sécurité.
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700">
                Identifiant PartnerPortal Fortinet <span className="text-[#D42B1E]">*</span>
              </Label>
              <Input
                required
                minLength={3}
                placeholder="Ex. FN-PARTNER-12345"
                value={form.partner_portal_id}
                onChange={onChange("partner_portal_id")}
                className="mt-1 rounded-sm font-mono text-sm"
                data-testid="register-partner-portal-input"
              />
              <p className="mt-1 text-[11px] text-zinc-500">
                Visible dans votre profil sur <span className="font-mono">partnerportal.fortinet.com</span>.
              </p>

              {/* Teaser — pas de Partner ID ? Procédure de récupération.
                  Présenté en details/summary pour ne pas alourdir le formulaire
                  pour les partenaires qui ont déjà leur ID en main. */}
              <details
                className="mt-3 group border border-amber-200 bg-amber-50/60 rounded-sm overflow-hidden"
                data-testid="register-no-partner-id-teaser"
              >
                <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer list-none select-none hover:bg-amber-100/60">
                  <HelpCircle className="h-4 w-4 text-amber-700 shrink-0" />
                  <span className="text-xs font-semibold text-amber-900">
                    Pas encore d'identifiant PartnerPortal&nbsp;?
                  </span>
                  <span className="ml-auto text-[10px] text-amber-700 font-semibold uppercase tracking-wide group-open:hidden">
                    Voir les options
                  </span>
                  <span className="ml-auto text-[10px] text-amber-700 font-semibold uppercase tracking-wide hidden group-open:inline">
                    Replier
                  </span>
                </summary>

                <div className="px-3 py-3 border-t border-amber-200 bg-white space-y-3 text-[12px] text-zinc-700 leading-relaxed">
                  <div>
                    <p className="font-semibold text-zinc-900 mb-1">
                      1. Vous n'êtes pas encore partenaire Fortinet
                    </p>
                    <p className="text-zinc-600">
                      Créez votre compte sur le portail officiel Fortinet pour
                      obtenir votre identifiant PartnerPortal puis revenez
                      finaliser votre inscription Pythagore.
                    </p>
                    <a
                      href="https://partnerportal.fortinet.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-[#D42B1E] hover:bg-[#B32218] text-white text-xs font-semibold rounded-sm transition-colors"
                      data-testid="register-partner-portal-link"
                    >
                      Ouvrir partnerportal.fortinet.com
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>

                  <div className="border-t border-zinc-100 pt-3">
                    <p className="font-semibold text-zinc-900 mb-1">
                      2. Vous êtes déjà partenaire Fortinet, mais sans ID en main
                    </p>
                    <p className="text-zinc-600">
                      Demandez votre Fortinet ID à <span className="font-mono">aly@fortinet.com</span>{" "}
                      en précisant que c'est pour accéder à Pythagore. Le bouton
                      ci-dessous prépare un e-mail directement&nbsp;:
                    </p>
                    <a
                      href={
                        "mailto:aly@fortinet.com" +
                        "?subject=" +
                        encodeURIComponent("Demande d'identifiant PartnerPortal — accès Pythagore") +
                        "&body=" +
                        encodeURIComponent(
                          `Bonjour Abdoulahi,

Je suis partenaire Fortinet et je souhaite accéder à l'application Pythagore (générateur de BOM/BOQ Fortinet) pour mon équipe avant-vente.

Pourrais-tu m'indiquer mon identifiant PartnerPortal afin de finaliser mon inscription sur https://fortinet-bom-config.preview.emergentagent.com ?

Coordonnées :
- Nom complet : ${form.full_name || "(à compléter)"}
- Société : ${form.company || "(à compléter)"}
- E-mail professionnel : ${form.email || "(à compléter)"}

Merci d'avance pour ton retour.

Cordialement,
${form.full_name || ""}`,
                        )
                      }
                      className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-semibold rounded-sm transition-colors"
                      data-testid="register-request-id-mailto"
                    >
                      <Mail className="h-3 w-3" />
                      Demander mon Fortinet ID
                    </a>
                  </div>
                </div>
              </details>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-700 flex items-center gap-2">
                <Award className="h-3.5 w-3.5 text-[#D42B1E]" />
                Certifications NSE détenues <span className="text-[#D42B1E]">*</span>
              </Label>
              <p className="mt-1 text-[11px] text-zinc-500">
                Cochez tous les niveaux validés. <strong>NSE 1, 2 et 3 sont obligatoires</strong> pour accéder au configurateur.
              </p>
              <div className="mt-3 grid grid-cols-4 gap-2" data-testid="register-nse-grid">
                {NSE_LEVELS.map((n) => {
                  const required = NSE_REQUIRED.includes(n);
                  const checked = nse.has(n);
                  return (
                    <label
                      key={n}
                      className={`flex items-center gap-2 px-3 py-2 border rounded-sm cursor-pointer transition-colors ${
                        checked
                          ? "border-[#D42B1E] bg-red-50"
                          : required
                            ? "border-amber-300 bg-amber-50/40 hover:bg-amber-50"
                            : "border-zinc-200 hover:bg-zinc-50"
                      }`}
                      data-testid={`register-nse-checkbox-${n}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleNse(n)}
                        className="data-[state=checked]:bg-[#D42B1E] data-[state=checked]:border-[#D42B1E]"
                      />
                      <span className="text-xs font-mono text-zinc-900">NSE {n}</span>
                      {required && (
                        <span className="text-[9px] text-[#D42B1E] font-semibold ml-auto">REQUIS</span>
                      )}
                    </label>
                  );
                })}
              </div>
              {missingRequired.length > 0 && (
                <p className="mt-2 text-[11px] text-[#D42B1E] font-medium">
                  Manquants : {missingRequired.map((n) => `NSE ${n}`).join(", ")}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || !formValid}
              className="w-full bg-[#D42B1E] hover:bg-[#B32218] rounded-sm h-11 disabled:opacity-50"
              data-testid="register-submit-button"
            >
              {loading ? "Soumission en cours..." : "Soumettre ma demande"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-center text-zinc-600">
            Déjà inscrit ? <Link to="/login" className="text-[#D42B1E] font-medium" data-testid="register-login-link">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
